/**
 * Dashboard Store - Zustand store for dashboard state management
 */
import { create } from 'zustand'
import { dashboardApi } from '../lib/dashboardApi'

export const useDashboardStore = create((set, get) => ({
  // State
  dashboards: [],
  templates: [],
  activeDashboard: null,
  widgets: {},
  preferences: {},
  loading: false,
  error: null,

  // Actions
  setDashboards: (dashboards) => set({ dashboards }),
  setTemplates: (templates) => set({ templates }),
  setActiveDashboard: (dashboard) => set({ activeDashboard: dashboard }),
  setWidgets: (dashboardId, widgets) => set((state) => ({
    widgets: { ...state.widgets, [dashboardId]: widgets }
  })),
  setPreferences: (projectId, preferences) => set((state) => ({
    preferences: { ...state.preferences, [projectId]: preferences }
  })),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Async thunks
  fetchTemplates: async () => {
    set({ loading: true, error: null })
    try {
      const response = await dashboardApi.getTemplates()
      set({ templates: response.data, loading: false })
    } catch (error) {
      set({ error: error.message, loading: false })
    }
  },

  fetchDashboards: async (projectId) => {
    set({ loading: true, error: null })
    try {
      const response = await dashboardApi.getDashboards(projectId)
      const activeDashboards = response.data.filter(d => !d.is_archived)
      set({
        dashboards: activeDashboards,
        activeDashboard: activeDashboards[0] || null,
        loading: false
      })
    } catch (error) {
      set({ error: error.message, loading: false })
    }
  },

  fetchDashboardWidgets: async (dashboardId) => {
    try {
      const response = await dashboardApi.getWidgets(dashboardId)
      get().setWidgets(dashboardId, response.data)
    } catch (error) {
      set({ error: error.message })
    }
  },

  createDashboard: async (projectId, templateId, name, description) => {
    try {
      const response = await dashboardApi.createDashboard({
        project: projectId,
        template: templateId,
        name,
        description
      })
      set((state) => ({
        dashboards: [...state.dashboards, response.data],
        activeDashboard: response.data
      }))
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  updateDashboard: async (dashboardId, data) => {
    try {
      const response = await dashboardApi.updateDashboard(dashboardId, data)
      set((state) => ({
        dashboards: state.dashboards.map(d => d.id === dashboardId ? response.data : d),
        activeDashboard: state.activeDashboard?.id === dashboardId ? response.data : state.activeDashboard
      }))
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  deleteDashboard: async (dashboardId) => {
    try {
      await dashboardApi.archiveDashboard(dashboardId)
      set((state) => ({
        dashboards: state.dashboards.filter(d => d.id !== dashboardId),
        activeDashboard: state.activeDashboard?.id === dashboardId ? state.dashboards[0] || null : state.activeDashboard
      }))
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  resetDashboard: async (dashboardId) => {
    try {
      const response = await dashboardApi.resetDashboard(dashboardId)
      set((state) => ({
        dashboards: state.dashboards.map(d => d.id === dashboardId ? response.data : d),
        activeDashboard: state.activeDashboard?.id === dashboardId ? response.data : state.activeDashboard,
        widgets: { ...state.widgets, [dashboardId]: response.data.widgets || [] }
      }))
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  duplicateDashboard: async (dashboardId, name) => {
    try {
      const response = await dashboardApi.duplicateDashboard(dashboardId, { name })
      set((state) => ({
        dashboards: [...state.dashboards, response.data],
        activeDashboard: response.data
      }))
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  addWidget: async (dashboardId, widgetData) => {
    try {
      const response = await dashboardApi.createWidget({
        dashboard: dashboardId,
        ...widgetData
      })
      set((state) => ({
        widgets: {
          ...state.widgets,
          [dashboardId]: [...(state.widgets[dashboardId] || []), response.data]
        }
      }))
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  removeWidget: async (dashboardId, widgetId) => {
    try {
      await dashboardApi.deleteWidget(widgetId)
      set((state) => ({
        widgets: {
          ...state.widgets,
          [dashboardId]: state.widgets[dashboardId]?.filter(w => w.id !== widgetId) || []
        }
      }))
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  reorderWidgets: async (dashboardId, widgets) => {
    try {
      const reorderData = widgets.map((w, idx) => ({ id: w.id, order: idx }))
      await dashboardApi.reorderWidgets(dashboardId, reorderData)
      set((state) => ({
        widgets: { ...state.widgets, [dashboardId]: widgets }
      }))
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  },

  savePreferences: async (projectId, preferences) => {
    try {
      const response = await dashboardApi.createPreference({
        project: projectId,
        ...preferences
      })
      get().setPreferences(projectId, response.data)
      return response.data
    } catch (error) {
      set({ error: error.message })
      throw error
    }
  }
}))
